/**
 * The back link shown on a Media Portal item page.
 *
 * A live-TV channel came from the TV Guide, so its back link returns there
 * instead of the Media Portal grid — but only when the TV Guide is actually
 * available to the viewer (same gate the nav applies to the TV Guide entry, so
 * the link never points at a page the nav hides). Every non-live item, and any
 * viewer without the TV Guide, keeps the Media Portal back link.
 *
 * The TV Guide target carries the viewer's provider/group filters as query
 * params so the guide reopens exactly as they left it.
 *
 * Pure and DOM-free so the choice is unit-testable in isolation; the caller
 * resolves `labelKey` through the streaming-pages catalog.
 */
export const MEDIA_PORTAL_PATH = "/streaming/dashboard/control-center/media";
export const TV_GUIDE_PATH = "/streaming/dashboard/control-center/tv-guide";

export interface MediaBackLink {
  to: string;
  /** Streaming-pages catalog key: `mediaPortal` = "Media Portal", `guideHeading` = "TV Guide". */
  labelKey: "mediaPortal" | "guideHeading";
}

export const resolveMediaBackLink = (params: {
  isLiveTv: boolean;
  tvGuideAvailable: boolean;
  /** TV Guide provider filter to restore (source id or `unknown`), if any. */
  provider?: string | null;
  /** TV Guide group filter to restore, if any. */
  group?: string | null;
}): MediaBackLink => {
  if (!(params.isLiveTv && params.tvGuideAvailable)) {
    return { to: MEDIA_PORTAL_PATH, labelKey: "mediaPortal" };
  }
  const search = new URLSearchParams();
  if (params.provider) search.set("provider", params.provider);
  if (params.group) search.set("group", params.group);
  const query = search.toString();
  return { to: query ? `${TV_GUIDE_PATH}?${query}` : TV_GUIDE_PATH, labelKey: "guideHeading" };
};
